import {
  sendAgentCcpEvent,
  sendAgentWebRtcBatch,
  sendAgentWebRtcCallReport,
  type AgentCcpEventRequest,
  type AgentWebRtcBatchRequest,
  type AgentWebRtcCallReportRequest,
  type AgentWebRtcSamplePayload,
} from './api'

const MAX_SAMPLES = 1200
const MAX_AGE_MS = 10 * 60 * 1000
const CHUNK = 120

type SampleItem = {
  kind: 'samples'
  contactId: string | null
  samples: AgentWebRtcSamplePayload[]
  queuedAt: number
}

type ReportItem = {
  kind: 'report'
  body: AgentWebRtcCallReportRequest
  queuedAt: number
}

type EventItem = {
  kind: 'event'
  body: AgentCcpEventRequest
  queuedAt: number
}

type OutboxItem = SampleItem | ReportItem | EventItem

export type TelemetryTransport = {
  sendSamples: (batch: AgentWebRtcBatchRequest) => Promise<boolean>
  sendCallReport: (report: AgentWebRtcCallReportRequest) => Promise<boolean>
  sendCcpEvent: (event: AgentCcpEventRequest) => Promise<boolean>
}

const defaultTransport: TelemetryTransport = {
  sendSamples: (batch) => sendAgentWebRtcBatch(batch),
  sendCallReport: (report) => sendAgentWebRtcCallReport(report),
  sendCcpEvent: (event) => sendAgentCcpEvent(event),
}

let transport: TelemetryTransport = defaultTransport
let items: OutboxItem[] = []
let flushPromise: Promise<void> | null = null
let flushAgain = false

export function __setTelemetryTransportForTests(next: TelemetryTransport | null): void {
  transport = next ?? defaultTransport
}

export function __resetTelemetryOutboxForTests(): void {
  items = []
  flushPromise = null
  flushAgain = false
  transport = defaultTransport
}

export function __telemetryOutboxSizeForTests(): {items: number; samples: number} {
  return {items: items.length, samples: sampleCount()}
}

export function enqueueWebRtcSamples(batch: AgentWebRtcBatchRequest): void {
  if (!batch.samples?.length) return
  items.push({
    kind: 'samples',
    contactId: batch.contactId ?? null,
    samples: batch.samples.slice(),
    queuedAt: Date.now(),
  })
  trim()
  void flushTelemetryOutbox()
}

export function enqueueWebRtcCallReport(report: AgentWebRtcCallReportRequest): void {
  items.push({kind: 'report', body: report, queuedAt: Date.now()})
  trim()
  void flushTelemetryOutbox()
}

export function enqueueCcpEvent(event: AgentCcpEventRequest): void {
  items.push({kind: 'event', body: event, queuedAt: Date.now()})
  trim()
  void flushTelemetryOutbox()
}

/** Send queued telemetry. Safe to call from hub onreconnected. */
export function flushTelemetryOutbox(): Promise<void> {
  if (flushPromise) {
    flushAgain = true
    return flushPromise.then(() => flushTelemetryOutbox())
  }
  flushPromise = (async () => {
    try {
      do {
        flushAgain = false
        await drain()
      } while (flushAgain)
    } finally {
      flushPromise = null
    }
  })()
  return flushPromise
}

function sampleCount(): number {
  let n = 0
  for (const item of items) {
    if (item.kind === 'samples') n += item.samples.length
  }
  return n
}

function trim(): void {
  const cutoff = Date.now() - MAX_AGE_MS
  items = items.filter((item) => item.queuedAt >= cutoff)
  let extra = sampleCount() - MAX_SAMPLES
  while (extra > 0) {
    const index = items.findIndex((item) => item.kind === 'samples')
    if (index < 0) break
    const item = items[index] as SampleItem
    if (item.samples.length <= extra) {
      extra -= item.samples.length
      items.splice(index, 1)
    } else {
      item.samples.splice(0, extra)
      extra = 0
    }
  }
}

async function drain(): Promise<void> {
  while (items.length > 0) {
    trim()
    const head = items[0]
    if (!head) return

    if (head.kind === 'samples') {
      const contactId = head.contactId
      const chunk: AgentWebRtcSamplePayload[] = []
      let index = 0
      while (
        chunk.length < CHUNK &&
        items[index]?.kind === 'samples' &&
        (items[index] as SampleItem).contactId === contactId
      ) {
        const item = items[index] as SampleItem
        const room = CHUNK - chunk.length
        chunk.push(...item.samples.slice(0, room))
        if (room < item.samples.length) break
        index += 1
      }
      const ok = await trySend(() => transport.sendSamples({contactId, samples: chunk}))
      if (!ok) return
      let remaining = chunk.length
      while (
        remaining > 0 &&
        items[0]?.kind === 'samples' &&
        (items[0] as SampleItem).contactId === contactId
      ) {
        const item = items[0] as SampleItem
        if (item.samples.length <= remaining) {
          remaining -= item.samples.length
          items.shift()
        } else {
          item.samples.splice(0, remaining)
          remaining = 0
        }
      }
      continue
    }

    items.shift()
    const ok =
      head.kind === 'report'
        ? await trySend(() => transport.sendCallReport(head.body))
        : await trySend(() => transport.sendCcpEvent(head.body))
    if (!ok) {
      items.unshift(head)
      return
    }
  }
}

async function trySend(send: () => Promise<boolean>): Promise<boolean> {
  try {
    return await send()
  } catch {
    return false
  }
}
